/**
 * Bridge legacy Bootstrap/Tippy-style confirmations and protected full-page
 * forms to the same ConfirmationDialog used by FormZero and Article.
 * Legacy confirmed mutations now submit authenticated POST requests.
 * Endpoint URLs/query parameters and controller responses remain unchanged.
 */
import DestructiveRequest from './DestructiveRequest.js';

export default class LegacyConfirmActions {
    constructor(dialog, {toastNotifier = null, translator = null, fetchHtml = null, modalManager = null,
        destructiveRequest = null} = {}) {
        this.dialog = dialog;
        this.toastNotifier = toastNotifier;
        this.translator = translator;
        this.fetchHtml = fetchHtml;
        this.modalManager = modalManager;
        this.destructiveRequest = destructiveRequest ?? new DestructiveRequest();
        this.resultModalCount = 0;
        this.pending = new WeakSet();
        this.approvedForms = new WeakSet();
        this.root = null;
        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    init(root = document) {
        if (this.root === root) return;
        if (this.root) this.destroy();
        this.root = root;
        // Capture is intentional: the older data-ajax-toggle bubble listener
        // must not run a destructive request before the dialog is accepted.
        root.addEventListener('click', this.onClick, true);
        root.addEventListener('submit', this.onSubmit, true);
    }

    destroy() {
        if (!this.root) return;
        this.root.removeEventListener('click', this.onClick, true);
        this.root.removeEventListener('submit', this.onSubmit, true);
        this.root = null;
    }

    isDisabled(node) {
        return !!(node.disabled || node.hasAttribute?.('disabled') ||
            node.classList?.contains('disabled') || node.getAttribute?.('aria-disabled') === 'true');
    }

    async onClick(event) {
        const trigger = event.target?.closest?.('[data-popper="confirm"], [data-destructive-post]');
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        if (this.isDisabled(trigger) || this.pending.has(trigger)) return;
        this.pending.add(trigger);
        try {
            const message = trigger.dataset.confirmBody || trigger.dataset.confirmMessage ||
                this.translator?.trans('confirm.message') || 'Confermare questa operazione?';
            const restoration = /ripristin|riattiv|undelet|restore|annullare la cancellazione/i.test(message);
            const danger = trigger.hasAttribute?.('data-destructive-post') ||
                (!restoration && (/elimin|cancell|delete|remove|rimuov|reset/i.test(message) ||
                /danger/.test(trigger.dataset.confirmClassOk || '')));
            const accepted = await this.dialog.ask(message, trigger, {
                destructive: danger, note: danger ? undefined : false,
            });
            if (!accepted) return;
            const toggle = trigger.getAttribute('data-ajax-toggle');
            if (toggle) {
                // Same AJAX behavior as the existing generic toggle handler,
                // but only AFTER the user has confirmed.
                const response = await this.destructiveRequest.post(toggle, {as: 'html'});
                const html = await response.text();
                if ('innerHTML' in trigger) trigger.innerHTML = html;
                this.fetchHtml?.executeScripts?.(html);
                this.toastNotifier?.success(this.translator?.trans('common.success') || 'Operazione completata');
                return;
            }
            const url = trigger.getAttribute('href') || trigger.getAttribute('data-href');
            if (!url || url === '#') {this.error(); return;}
            if (trigger.getAttribute('data-dialog-name') && this.modalManager) {
                // Legacy confirm-modal actions show their response in a Bootstrap
                // result modal. Keep that separate from the shared confirmation.
                trigger.setAttribute('data-modal-id', `confirmed-action-${++this.resultModalCount}`);
                trigger.setAttribute('data-modal-title', trigger.getAttribute('aria-label') ||
                    this.translator?.trans('common.info') || 'Risultato');
                trigger.setAttribute('data-modal-size', 'modal-lg');
                trigger.setAttribute('data-modal-source', url);
                trigger.setAttribute('data-modal-reload', 'true');
                const node = this.modalManager.createModal(trigger);
                this.modalManager.cleanupModal(node, 'true');
                const response = await this.destructiveRequest.post(url, {as: 'html'});
                const body = node.querySelector('[data-modal-section="body"]');
                body.innerHTML = await response.text();
                this.modalManager.executeScripts?.(body);
                body.querySelectorAll('form').forEach(form => this.modalManager.handleForm(form, node));
                return;
            }
            // Confirmed legacy mutations must use POST, preserving their URL
            // query parameters (record IDs) and the existing redirect contract.
            await this.destructiveRequest.submit(url);
        } catch (error) {
            console.error('Confirmed action failed:', error);
            this.error();
        } finally {
            this.pending.delete(trigger);
        }
    }

    async onSubmit(event) {
        const form = event.target;
        const message = form?.dataset?.confirmOnSubmit;
        if (!message || this.approvedForms.has(form)) {
            this.approvedForms.delete(form);
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (this.pending.has(form)) return;
        this.pending.add(form);
        const submitter = event.submitter;
        try {
            const accepted = await this.dialog.ask(message, submitter || form, {
                destructive: form.dataset.confirmDestructive !== 'false',
                note: form.dataset.confirmDestructive === 'false' ? false : undefined,
            });
            if (!accepted) return;
            this.approvedForms.add(form);
            if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
            else form.submit();
            // requestSubmit can be prevented by client-side validation.
            // Don't leave a bypass token that could authorize a later attempt.
        } catch (error) {
            console.error('Confirmed form submission failed:', error);
            this.error();
        } finally {
            this.approvedForms.delete(form);
            this.pending.delete(form);
        }
    }

    error() {
        this.toastNotifier?.error(this.translator?.trans('common.error') || 'Impossibile completare l’operazione');
    }
}
